// @ts-strict-ignore
import {
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Typography,
    useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { useMemo } from 'react';

const ONE_DAY = 1000 * 60 * 60 * 24;

export interface DateExtensionSelectorProps {
    currentEndDate?: Date | null;
    value: number | null;
    onChange: (val: number) => void;
    maxDays?: number;
    label: React.ReactNode;
}

export function DateExtensionSelector({
    currentEndDate,
    value,
    onChange,
    maxDays = 14,
    label,
}: DateExtensionSelectorProps) {
    const now = new Date();
    const theme = useTheme();
    const { formatMessage, formatDate } = useIntl();

    const daysList = useMemo(() => {
        const now = new Date();
        const base = currentEndDate
            ? Math.max(
                  0,
                  Math.ceil(
                      (currentEndDate.getTime() - now.getTime()) / ONE_DAY,
                  ),
              )
            : 0;
        return Array.from({ length: base + maxDays + 1 }, (_, k) => k);
    }, [currentEndDate, maxDays]);

    return (
        <FormControl fullWidth margin="normal">
            <InputLabel>{label}</InputLabel>
            <Select
                value={value ?? ''}
                onChange={(e) => onChange(+e.target.value)}
                label={label}
                name="extendDays"
            >
                {daysList.map((dayCount, index) => {
                    const newDate = new Date(
                        now.getTime() + dayCount * ONE_DAY,
                    );
                    const isCurrentEndDate =
                        currentEndDate &&
                        newDate.toDateString() ===
                            currentEndDate.toDateString();
                    const isLast = index === daysList.length - 1;

                    return (
                        <MenuItem
                            value={dayCount}
                            key={dayCount}
                            disabled={!!currentEndDate && isCurrentEndDate}
                            autoFocus={isLast}
                            sx={{
                                '&.Mui-disabled': {
                                    color: theme.bino.color.purple,
                                    backgroundColor: theme.bino.color.lightGray,
                                    opacity: 0.8,
                                },
                            }}
                        >
                            <ListItemText
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    alignItems: 'center',
                                    columnGap: 2,
                                }}
                                primary={
                                    dayCount === 0
                                        ? formatMessage({
                                              defaultMessage: 'Today',
                                          })
                                        : formatDate(newDate, {
                                              year: 'numeric',
                                              month: 'long',
                                              day: 'numeric',
                                              weekday: 'short',
                                          })
                                }
                                secondary={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                            color: isCurrentEndDate
                                                ? theme.bino.color.lightPurple
                                                : theme.bino.color.gray,
                                            opacity: isCurrentEndDate ? 0.8 : 1,
                                            display: {
                                                xs: 'none',
                                                '@media (min-width: 400px)': {
                                                    display: 'block',
                                                },
                                            },
                                        }}
                                    >
                                        {dayCount === 0
                                            ? null
                                            : formatMessage(
                                                  {
                                                      defaultMessage:
                                                          'In {days, plural, one {# day} other {# days}}',
                                                  },
                                                  { days: dayCount },
                                              )}
                                        {isCurrentEndDate && (
                                            <span
                                                style={{
                                                    marginLeft: 8,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {formatMessage({
                                                    defaultMessage:
                                                        'Current end date',
                                                })}
                                            </span>
                                        )}
                                    </Typography>
                                }
                            />
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
}
